import React, { createContext, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useHistory, useParams } from 'react-router-dom';
import { useLazyQuery, useMutation } from '@apollo/client';
import * as yup from 'yup';
import { isValidPhoneNumber } from 'react-phone-number-input';
import { GET_EVENTS_LIST, GET_EVENT_ITEM } from '../../../network/queries';
import { COVID_QUESTIONS, COVID_QUESTIONS_ERROR } from '../../../constants/questions';
import { SUBMIT_RSVP } from '../../../network/mutations';

export const RSVPContext = createContext({});

export const RSVPProvider = ({ children }) => {
  const { virtualPhoneNumber: vpn } = useParams();
  const history = useHistory();

  const [currentStep, setCurrentStep] = useState(0);
  const [validationError, setValidationError] = useState([]);
  const [usedFields, setUsedFields] = useState([]);
  const [questionsData, setQuestionsData] = useState({});
  const [validationQuestionErrors, setValidationQuestionErrors] = useState([]);

  const goToList = () => {
    setCurrentStep(0);
    history.push(`/e/${vpn}`);
  };

  const [fields, setFields] = useState({
    firstName: '',
    lastName: '',
    gender: '',
    zipCode: '',
    primaryPhoneNumber: '',
  });

  const schema = yup.object().shape({
    firstName: yup.string().required('Oops! First Name can`t be blank.'),
    lastName: yup.string().required('Oops! Last Name can`t be blank.'),
    zipCode: yup
      .string()
      .matches(/^\d{5}$|^\d{5}-\d{4}$/, {
        message: 'Please enter correct value',
      })
      .required('Oops! Zip/Postal can`t be blank.'),
    gender: yup.string().required('Please select an option.'),
    primaryPhoneNumber: yup.string().test({
      name: 'primaryPhoneNumber',
      message: 'Phone number is not valid',
      test: (value) => isValidPhoneNumber(`${value}`),
    }),
  });

  // ====================================
  // Validation

  const handleFormValidation = (formFields) => {
    schema.validate(formFields, { abortEarly: false }).then(
      () => {
        setValidationError([]);
      },
      (response) => {
        console.log(response.inner);
        setValidationError(response.inner);
      },
    );
  };

  useEffect(() => {
    handleFormValidation({ ...fields });
  }, [fields]);

  const updateUsedFields = (name) => {
    if (!usedFields.includes(name)) {
      usedFields.push(name);
      setUsedFields(usedFields);
      handleFormValidation(fields);
    }
  };

  const hasErrorMessage = (name) => {
    const messages = [];
    validationError.forEach((e) => {
      if (e.path === name && usedFields.includes(name)) {
        messages.push(e.message);
      }
    });
    return messages;
  };

  const questionsSchema = yup.object().shape({
    question_1: yup
      .string()
      .required('Oops! Field can`t be blank.')
      .test('no', COVID_QUESTIONS_ERROR, (value) => value === 'no'),
    question_2: yup
      .string()
      .required('Oops! Field can`t be blank.')
      .test('no', COVID_QUESTIONS_ERROR, (value) => value === 'no'),
    question_3: yup
      .string()
      .required('Oops! Field can`t be blank.')
      .test('no', COVID_QUESTIONS_ERROR, (value) => value === 'no'),
    question_4: yup
      .string()
      .required('Oops! Field can`t be blank.')
      .test('no', COVID_QUESTIONS_ERROR, (value) => value === 'no'),
    question_5: yup
      .string()
      .required('Oops! Field can`t be blank.')
      .test('no', COVID_QUESTIONS_ERROR, (value) => value === 'no'),
  });

  const handleQuestionsValidation = (formFields) => {
    questionsSchema.validate(formFields, { abortEarly: false }).then(
      () => {
        setValidationQuestionErrors([]);
      },
      (response) => {
        setValidationQuestionErrors(response.inner);
      },
    );
  };

  useEffect(() => {
    handleQuestionsValidation({});
  }, []);

  const setQuestionAnswer = (name, value) => {
    const newQuestionsData = { ...questionsData, [name]: value };
    setQuestionsData(newQuestionsData);
    handleQuestionsValidation(newQuestionsData);
  };

  const handleUpdateValue = (value, name) => {
    setFields({ ...fields, [name]: value });
  };

  const [submitRsvpRequest, {
    loading: mutationLoading,
    error: mutationError,
  }] = useMutation(
    SUBMIT_RSVP,
  );

  const handleUpdateFields = ({ onSuccess, onError }) => {
    const {
      eventId,
      firstName,
      lastName,
      zipCode,
      primaryPhoneNumber,
      gender,
    } = fields;

    const inputData = {
      eventId,
      firstName,
      lastName,
      zipCode,
      primaryPhoneNumber,
      gender,
    };

    submitRsvpRequest({
      variables: { input: inputData },
    }).then(
      (result) => {
        if (typeof onSuccess === 'function') {
          onSuccess(inputData, result);
        } else {
          console.log(result);
        }
      },
      (error) => {
        if (typeof onError === 'function') {
          onError(error);
        } else {
          console.log(error);
        }
      },
    );
  };

  const [getEvents, {
    loading: eventsListLoading,
    error: eventsListError,
    data: eventsListData,
  }] = useLazyQuery(GET_EVENTS_LIST, {
    variables: { vpn },
  });

  const [getEventDetails, {
    loading: eventItemLoading,
    error: eventItemError,
    data: eventItemData,
  }] = useLazyQuery(GET_EVENT_ITEM);

  const getEventData = (eventId) => {
    getEventDetails({
      variables: {
        vpn,
        eventId,
      },
    });
  };

  const goToNextStep = () => {
    setCurrentStep(currentStep + 1);
  };

  const goToLastStep = () => {
    setCurrentStep(3);
  };

  const contextData = {
    getEvents,
    eventsListLoading,
    eventsListError,
    eventsListData,

    getEventData,
    eventItemLoading,
    eventItemError,
    eventItemData,

    currentStep,
    setCurrentStep,
    goToNextStep,
    goToLastStep,
    goToList,

    formData: fields,
    setField: handleUpdateValue,
    setFormData: setFields,
    saveData: handleUpdateFields,
    validationError,
    isFormValid: validationError && validationError.length === 0,
    updateUsedFields,
    hasErrorMessage,

    questions: COVID_QUESTIONS,
    questionsData,
    isQuestionsFormValid: validationQuestionErrors.length === 0,
    setQuestionAnswer,
    validationQuestionErrors,

    mutationLoading,
    mutationError,
  };

  return <RSVPContext.Provider value={{ contextData }}>{children}</RSVPContext.Provider>;
};

RSVPProvider.propTypes = {
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]).isRequired,
};
